import { createSlice } from '@reduxjs/toolkit';

/**
 * Session Validation Redux Slice
 *
 * Stores risk assessment results from Verisoul session validation.
 *
 * validationResult structure:
 * - riskScore: number (0-1, higher = more risky)
 * - riskLevel: 'low' | 'medium' | 'high'
 * - decision: 'Real' | 'Fake' | null
 * - allowed: boolean (whether the user is allowed based on risk)
 */
const initialState = {
  validationResult: null, // Stores the validation result (authenticated or unauthenticated)
  sessionId: null, // Verisoul session ID
  isLoading: false,
  error: null,
  lastChecked: null, // Timestamp of last check
  type: null, // 'authenticated' | 'unauthenticated' | null
};

const sessionValidationSlice = createSlice({
  name: 'sessionValidation',
  initialState,
  reducers: {
    setValidationResult: (state, action) => {
      state.validationResult = action.payload.result;
      state.type = action.payload.type; // 'authenticated' or 'unauthenticated'
      state.lastChecked = Date.now();
      state.isLoading = false;
      state.error = null;
    },
    setSessionId: (state, action) => {
      state.sessionId = action.payload;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload ?? true;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    clearValidationResult: (state) => {
      state.validationResult = null;
      state.sessionId = null;
      state.type = null;
      state.lastChecked = null;
      state.error = null;
      state.isLoading = false;
    },
  },
});

export const {
  setValidationResult,
  setSessionId,
  setLoading,
  setError,
  clearValidationResult,
} = sessionValidationSlice.actions;

// Selectors
export const selectValidationResult = (state) => state.sessionValidation.validationResult;
export const selectSessionId = (state) => state.sessionValidation.sessionId;
export const selectValidationType = (state) => state.sessionValidation.type;
export const selectIsLoadingValidation = (state) => state.sessionValidation.isLoading;
export const selectValidationError = (state) => state.sessionValidation.error;
export const selectLastChecked = (state) => state.sessionValidation.lastChecked;

export default sessionValidationSlice.reducer;
